import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { liveQuery, type Subscription } from "dexie";

import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { WithAppStore } from "@/store/types";
import {
  AudioRecordingModelData,
  AudioRecordingRecordingStatus,
  AudioRecordingProcessingStatus,
  AudioRecordingUploadStatus,
  AudioRecordingAppEvent,
  AudioRecordingAppEventKind,
} from "./types";
import type { LocalAudioRecordingTranscriptTurnRecord } from "@/domains/dexie-db/models/audio-recording";
import type { Uuid } from "@/domains/global/identifiers";
import { clientEnvModule } from "@/modules/client-env";

type ConstructorParams = {
  id: string;
  data: SyncUpdateValue<AudioRecordingModelData>;
} & WithAppStore;

export class AudioRecordingObservable extends BaseSyncModel<AudioRecordingModelData> {
  public modelKind = SyncModelKind.AudioRecording;
  private durationSubscription?: Subscription;
  private transcriptSubscription?: Subscription;
  durationMs: number = 0;
  private localTranscriptTurns: LocalAudioRecordingTranscriptTurnRecord[] = [];

  constructor({ id, data, store }: ConstructorParams) {
    super({ id, data, store });

    makeObservable<
      AudioRecordingObservable,
      "durationSubscription" | "transcriptSubscription" | "resetDuration" | "localTranscriptTurns"
    >(this, {
      associatedClientId: true,
      modelKind: false,
      recordingStatus: computed,
      uploadStatus: computed,
      processingStatus: computed,
      locallyCreatedAt: computed,
      locallyModifiedAt: computed,
      valueJson: computed,
      appEvents: computed,
      hasAppEvent: false,
      wasTranscriptionCompromised: computed,
      associatedNoteId: computed,
      durationMs: observable,
      formattedDuration: computed,
      subscribeToDuration: action,
      unsubscribeFromDuration: action,
      durationSubscription: observable.ref,
      transcriptTurns: computed,
      subscribeToTranscripts: action,
      unsubscribeFromTranscripts: action,
      transcriptSubscription: observable.ref,
      localTranscriptTurns: observable.shallow,
      resetDuration: action,
      uploadProgressPercent: computed,
      uploadErrorMessage: computed,
      transformErrorMessage: computed,
      isTransforming: computed,
      hasActiveSession: computed,
      isRecording: computed,
      isUploading: computed,
      isProcessing: computed,
      hasFinishedProcessing: computed,
      hasFailedProcessing: computed,
      hasFailedUploading: computed,
      hasFinishedRecording: computed,
      hasStartedRecording: computed,
      hasBeenCompleted: computed,
      hasNotBeenCompleted: computed,
      trashedAt: computed,
      isTrashed: computed,
      isAwaitingTransformRemoteUpdate: computed,
      setAcknowledgedTransformRemoteUpdate: action,
      hasValidAssociatedNote: computed,
      isFromCurrentClientDevice: false,
    });

    onBecomeObserved(this, "formattedDuration", () => this.subscribeToDuration());
    onBecomeUnobserved(this, "formattedDuration", () => this.unsubscribeFromDuration());

    onBecomeObserved(this, "transcriptTurns", () => this.subscribeToTranscripts());
    onBecomeUnobserved(this, "transcriptTurns", () => this.unsubscribeFromTranscripts());
  }

  get hasActiveSession(): boolean {
    return this.store.audio.activeRecordingId === this.id;
  }

  get hasValidAssociatedNote(): boolean {
    const noteId = this.associatedNoteId as Uuid | undefined;
    if (!noteId) return false;

    const note = this.store.notes.getNoteObservableById({ noteId });
    if (note) {
      return !note.isDeleted && !note.isTrashed;
    }

    if (this.store.notes.doesNotExist(noteId)) {
      return false;
    }

    return true;
  }

  get hasBeenCompleted(): boolean {
    return this.hasFinishedProcessing;
  }

  get hasNotBeenCompleted(): boolean {
    return !this.hasBeenCompleted;
  }

  get isRecording(): boolean {
    return this.recordingStatus === AudioRecordingRecordingStatus.IN_PROGRESS;
  }

  get isUploading(): boolean {
    return this.uploadStatus === AudioRecordingUploadStatus.IN_PROGRESS;
  }

  get hasFailedUploading(): boolean {
    return this.uploadStatus === AudioRecordingUploadStatus.ERROR;
  }

  get isProcessing(): boolean {
    return this.processingStatus === AudioRecordingProcessingStatus.IN_PROGRESS;
  }

  get hasFinishedProcessing(): boolean {
    return this.processingStatus === AudioRecordingProcessingStatus.COMPLETED;
  }

  get hasFailedProcessing(): boolean {
    return this.processingStatus === AudioRecordingProcessingStatus.ERROR;
  }

  get trashedAt(): string | null {
    return this.modelData.trashed_at ?? null;
  }

  get isTrashed(): boolean {
    return this.trashedAt !== null;
  }

  get hasFinishedRecording(): boolean {
    return this.recordingStatus === AudioRecordingRecordingStatus.COMPLETED;
  }

  get hasStartedRecording(): boolean {
    return this.recordingStatus !== AudioRecordingRecordingStatus.INITIAL;
  }

  get recordingStatus(): AudioRecordingModelData["recording_status"] {
    return this.modelData.recording_status;
  }

  get uploadStatus(): AudioRecordingModelData["upload_status"] {
    return this.modelData.upload_status;
  }

  get processingStatus(): AudioRecordingModelData["processing_status"] {
    return this.modelData.processing_status;
  }

  get locallyCreatedAt(): string {
    return this.modelData.locally_created_at;
  }

  get locallyModifiedAt(): string {
    return this.modelData.locally_modified_at;
  }

  get valueJson(): AudioRecordingModelData["value_json"] | undefined {
    return this.modelData.value_json;
  }

  /**
   * Returns the app_events array from value_json, typed as AudioRecordingAppEvent[].
   * These events track the lifecycle of the recording (start, pause, resume, complete, errors, etc.)
   */
  get appEvents(): AudioRecordingAppEvent[] {
    const events = this.modelData.value_json?.app_events;
    if (!Array.isArray(events)) return [];
    return events as AudioRecordingAppEvent[];
  }

  /**
   * Checks if this recording has an app event of the specified kind.
   */
  hasAppEvent(kind: AudioRecordingAppEventKind): boolean {
    return this.appEvents.some(event => event.kind === kind);
  }

  /**
   * Returns true if transcription was compromised during this recording
   * (i.e., max reconnection attempts were exceeded).
   */
  get wasTranscriptionCompromised(): boolean {
    return this.hasAppEvent(AudioRecordingAppEventKind.TRANSCRIPTION_COMPROMISED);
  }

  get associatedNoteId(): string | undefined {
    return this.modelData.associated_note_id ?? undefined;
  }

  get associatedClientId(): string | undefined {
    return this.modelData.associated_client_id ?? undefined;
  }

  get formattedDuration(): string {
    const totalMs = this.durationMs;
    if (!totalMs) return "00:00";

    const minutes = Math.floor(totalMs / 60000);
    const seconds = Math.floor((totalMs % 60000) / 1000);

    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  get transcriptTurns(): LocalAudioRecordingTranscriptTurnRecord[] {
    return this.localTranscriptTurns;
  }

  subscribeToDuration() {
    this.unsubscribeFromDuration();

    this.durationSubscription = liveQuery(async () => {
      /**
       * Note that we do load all blobs here, which could become a bit slow.
       * However, after testing, it seems performant, so I'm going to avoid over-complicating it for now.
       * (If needed, we can persist some data at write time to avoid re-summing every time).
       */
      const blobs = await this.store.dexie.repositories.localAudioRecordingBlobs.table
        .where({ associatedAudioRecordingId: this.id })
        .toArray();

      return blobs.reduce((total, blob) => total + (blob.durationMs ?? 0), 0);
    }).subscribe({
      next: totalDurationMs => {
        runInAction(() => {
          this.durationMs = totalDurationMs ?? 0;
        });
      },
      error: () => {
        this.resetDuration();
      },
    });
  }

  unsubscribeFromDuration() {
    this.durationSubscription?.unsubscribe();
    this.durationSubscription = undefined;
  }

  subscribeToTranscripts() {
    this.unsubscribeFromTranscripts();

    this.transcriptSubscription = liveQuery(async () => {
      const turns = await this.store.dexie.repositories.localAudioRecordingTranscriptTurns.table
        .where({ associatedAudioRecordingId: this.id })
        .sortBy("transcriptTurnIndex");

      return turns;
    }).subscribe({
      next: turns => {
        runInAction(() => {
          this.localTranscriptTurns = turns ?? [];
        });
      },
      error: () => {
        runInAction(() => {
          this.localTranscriptTurns = [];
        });
      },
    });
  }

  unsubscribeFromTranscripts() {
    this.transcriptSubscription?.unsubscribe();
    this.transcriptSubscription = undefined;
  }

  private resetDuration() {
    this.durationMs = 0;
  }

  get uploadProgressPercent(): number | null {
    return this.store.audio.getUploadStateForRecording({ audioRecordingId: this.id })
      .uploadProgressPercent;
  }

  get uploadErrorMessage(): string | null {
    return this.store.audio.getUploadStateForRecording({ audioRecordingId: this.id }).errorMessage;
  }

  get transformErrorMessage(): string | null {
    return this.store.audio.getTransformStateForRecording({ audioRecordingId: this.id })
      .errorMessage;
  }

  get isTransforming(): boolean {
    return this.store.audio.getTransformStateForRecording({ audioRecordingId: this.id })
      .isTransforming;
  }

  /**
   * This state means that:
   * - We successfully called the transform API...
   * - ...but we are waiting for the sync model updates to flow back to the app.
   */
  get isAwaitingTransformRemoteUpdate(): boolean {
    return this.store.audio.getTransformStateForRecording({ audioRecordingId: this.id })
      .awaitingRemoteUpdate;
  }

  async isFromCurrentClientDevice(): Promise<boolean> {
    const { clientGroupId } = await clientEnvModule.clientGroupId();
    return this.associatedClientId === clientGroupId;
  }

  setAcknowledgedTransformRemoteUpdate() {
    this.store.audio.setAcknowledgedTransformRemoteUpdate({ audioRecordingId: this.id });
  }
}
